import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { useSpring, interpolate, config } from "react-spring";
import useIdle from "../hooks/useIdle";
import Modal from "./UI/Modal";
import PrimaryButton from "./UI/Button/Primary";
import Backdrop from "./UI/Backdrop";
import Countdown from "./Countdown";
import { selectInactivityLogoutTimeout } from "../selector/preferencesSelector";

const LOGOUT_TIMEOUT = 60 * 1000;
const DEFAULT_IDLE_TIMEOUT = 20 * 60 * 1000; // 20 minutes

const Container = styled(Backdrop)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AutomaticInactivityLogout = ({ history, timeout }) => {
  const idle = useIdle(timeout === null ? DEFAULT_IDLE_TIMEOUT : timeout);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (idle) {
      setLoggingOut(true);
    }
  }, [idle]);

  useEffect(() => {
    if (loggingOut) {
      const id = setTimeout(() => {
        history.push("/logout");
      }, LOGOUT_TIMEOUT);
      return () => clearTimeout(id);
    }
  }, [loggingOut]); // eslint-disable-line react-hooks/exhaustive-deps

  const keepLoggedIn = useCallback(() => {
    setLoggingOut(false);
  }, []);

  useEffect(() => {
    if (loggingOut) {
      document.body.addEventListener("keyup", keepLoggedIn, true);
      return () => {
        document.body.removeEventListener("keyup", keepLoggedIn, true);
      };
    }
  }, [keepLoggedIn, loggingOut]);

  const { scale, ...style } = useSpring({
    config: config.stiff,
    from: { scale: 0, opacity: 0 },
    to: { scale: loggingOut ? 1 : 0, opacity: loggingOut ? 1 : 0 }
  });

  return loggingOut ? (
    <Container>
      <Modal
        style={{
          ...style,
          transform: interpolate([scale], s => `scaleY(${s})`)
        }}
        title={
          <span>
            You will be logged out automatically in{" "}
            <Countdown start={LOGOUT_TIMEOUT} /> seconds
          </span>
        }
        content="You will be logged out automatically due to inactivity. Press any key to dismiss this alert."
        onClose={keepLoggedIn}
        buttons={<PrimaryButton onClick={keepLoggedIn}>I'm back</PrimaryButton>}
      />
    </Container>
  ) : null;
};

AutomaticInactivityLogout.propTypes = {
  timeout: PropTypes.number
};

AutomaticInactivityLogout.defaultProps = {
  timeout: DEFAULT_IDLE_TIMEOUT
};

const mapStateToProps = state => ({
  timeout: selectInactivityLogoutTimeout(state)
});

export default connect(mapStateToProps)(withRouter(AutomaticInactivityLogout));
